// Porta de correcao de venda finalizada no CAIXA ERRADO (DA-351, 2026-07-21).
//
// Ate aqui nao havia porta nenhuma: o unico UPDATE de `venda_pagamentos.caixa_id` fora da
// finalizacao era o "Conferir" da Conciliacao Bancaria, fechado pra dinheiro em 4 camadas e que
// some assim que a venda concilia. O caso real (VND-001210, R$ 21,00 da CRISTINA que caiu no
// "Movimento de Caixa" em vez da gaveta dela) teve que ser resolvido no banco a mao.
//
// Peca UNICA usada em dois lugares: a coluna ACOES da aba "Vendas hoje" (pega no flagrante) e a
// aba Turnos (alcanca qualquer dia, que e como o erro costuma aparecer — dias depois, no turno
// que nao fecha). Recebe uma LISTA porque venda mista tem uma linha por forma e so uma delas
// costuma estar no lugar errado; com uma linha so, o seletor nao aparece.
import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../Modal';
import { api } from '../../lib/api';
import { useDialog } from '../DialogProvider';
import { formatMoeda, formatDataHoraSP } from '@fiodecobre/shared';

export interface PagamentoMovivel {
  id: number;
  venda_numero: string;
  cliente_nome: string;
  forma_nome: string;
  forma_pagamento_id: number;
  valor: number;
  caixa_id: number | null;
  caixa_nome: string | null;
  finalizada_em: string;
}

interface ContaOpcao {
  id: number;
  nome: string;
  formas_pagamento?: Array<{ id: number }>;
}

export function ModalMoverPagamento({ pagamentos, contas, onClose, onMovido }: {
  /** null/vazio = modal fechado. */
  pagamentos: PagamentoMovivel[] | null;
  contas: ContaOpcao[];
  onClose: () => void;
  onMovido: () => void;
}) {
  const lista = pagamentos ?? [];
  const [pagamentoId, setPagamentoId] = useState<number | null>(null);
  const [destino, setDestino] = useState<number | ''>('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const dialog = useDialog();

  // Reabrir o modal em outra venda tem que zerar as escolhas da anterior — senao o destino
  // escolhido pra uma venda vaza pra proxima (e o select mostraria caixa de outra forma).
  const chave = lista.map((p) => p.id).join(',');
  useEffect(() => {
    setPagamentoId(lista.length === 1 ? lista[0]!.id : null);
    setDestino('');
    setMotivo('');
  }, [chave]); // eslint-disable-line react-hooks/exhaustive-deps

  const pagamento = lista.find((p) => p.id === pagamentoId) ?? null;

  // So os caixas que ACEITAM a forma (mesma regra do "Caixa destino" da finalizacao) e que nao
  // sejam o atual. O backend cobra o mesmo — a trava do front nao pode ser a unica.
  const destinos = useMemo(() => {
    if (!pagamento) return [];
    return contas
      .filter((c) => c.id !== pagamento.caixa_id)
      .filter((c) => c.formas_pagamento?.some((f) => f.id === pagamento.forma_pagamento_id));
  }, [contas, pagamento]);

  if (!pagamentos || lista.length === 0) return null;

  async function confirmar() {
    if (!pagamento || destino === '') { void dialog.alerta('Escolha o caixa pra onde o pagamento deve ir.'); return; }
    setEnviando(true);
    try {
      const r = await api.post<{ ok: boolean; destino_nome: string; valor: number }>(
        `/api/caixa/pagamentos/${pagamento.id}/mover`,
        { caixa_destino_id: destino, motivo: motivo.trim() || null },
      );
      dialog.toast(`${formatMoeda(r.valor)} movido para ${r.destino_nome}`);
      onMovido();
      onClose();
    } catch (e: any) {
      // Solta o botao ANTES do alerta: o `dialog.alerta` e uma promise que so resolve no OK, entao
      // no `finally` o botao ficaria escrito "Movendo..." atras do modal de erro — parecendo que a
      // operacao seguiu em andamento depois de ter sido recusada.
      setEnviando(false);
      await dialog.alerta(e?.message || 'Nao foi possivel mover o pagamento.');
      return;
    }
    setEnviando(false);
  }

  const rotulo = { fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 } as const;
  const cabecalho = lista[0]!;

  return (
    <Modal aberto onFechar={onClose} titulo="Corrigir o caixa da venda" largura={520} compacto>
      <div style={{ display: 'grid', gap: 14 }}>
        {/* O que esta sendo movido — o operador precisa reconhecer a venda antes de confirmar. */}
        <div style={{ border: '1px solid var(--input-border)', borderRadius: 6, padding: '10px 12px', fontSize: 13, display: 'grid', gap: 4 }}>
          <div><strong>{cabecalho.venda_numero}</strong> · {cabecalho.cliente_nome}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{formatDataHoraSP(cabecalho.finalizada_em)}</div>
        </div>

        {lista.length > 1 && (
          <div>
            <label style={rotulo}>Qual pagamento</label>
            <div style={{ display: 'grid', gap: 6 }}>
              {lista.map((p) => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={pagamentoId === p.id}
                    onChange={() => { setPagamentoId(p.id); setDestino(''); }}
                    disabled={enviando}
                    style={{ width: 'auto' }}
                  />
                  <span>{p.forma_nome} · <strong className="cel-num">{formatMoeda(p.valor)}</strong></span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>em {p.caixa_nome ?? '—'}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {pagamento && lista.length === 1 && (
          <div style={{ fontSize: 13 }}>
            {pagamento.forma_nome} · <strong className="cel-num">{formatMoeda(pagamento.valor)}</strong>
            {' '}— hoje esta em <strong>{pagamento.caixa_nome ?? '—'}</strong>
          </div>
        )}

        <div>
          <label style={rotulo}>Mover para</label>
          <select
            value={String(destino)}
            onChange={(e) => setDestino(e.target.value === '' ? '' : Number(e.target.value))}
            style={{ width: '100%' }}
            disabled={enviando || !pagamento}
          >
            <option value="">Selecione...</option>
            {destinos.map((c) => <option key={c.id} value={c.id}>{c.nome.toUpperCase()}</option>)}
          </select>
          {pagamento && destinos.length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Nenhum outro caixa aceita pagamento em {pagamento.forma_nome.toLowerCase()}.
            </div>
          )}
        </div>

        <div>
          <label style={rotulo}>Motivo <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span></label>
          <input
            type="text"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: escolhi a gaveta errada na finalizacao"
            style={{ width: '100%' }}
            disabled={enviando}
          />
        </div>

        {/* Nao e detalhe tecnico: o turno daquele dia PODE ter fechado, e o numero dele vai mudar.
            Quem assina o fechamento merece saber disso antes, nao descobrir depois. */}
        <div style={{ backgroundColor: '#fde68a', color: '#000', borderLeft: '6px solid #ca8a04', borderRadius: 6, padding: '10px 12px', fontSize: 12, lineHeight: 1.45 }}>
          O pagamento vai pro turno daquele caixa <strong>no dia da venda</strong>. Se o turno ja
          fechou, o <strong>esperado</strong> dele e recalculado — o valor <strong>contado</strong> na
          gaveta nunca muda. Fica tudo registrado com o seu nome.
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <button type="button" className="btn-secundario" onClick={onClose} disabled={enviando}>Cancelar</button>
        <button type="button" className="btn-primario" onClick={() => void confirmar()} disabled={enviando || destino === '' || !pagamento}>
          {enviando ? 'Movendo...' : 'Mover pagamento'}
        </button>
      </div>
    </Modal>
  );
}
